/**
 * Desc Parent and Child(ren) — the dashboard detail page for the
 * /report-management/descendant-parents-and-children report.
 *
 * Read the way the family reads: the Start/Top of Line couple first, with
 * their children under them, each named with the line they head; then line
 * by line, oldest head to youngest, every parent in that line with their
 * children under them —
 *
 *        Parents: John & Mary Witherspoon
 *     12 Joseph, Sr. & Frances Witherspoon • Silver Line
 *         Mary M      87  • Dk Blue Line
 *         Ida         84  • Dk Green Line
 *
 *  9  Mary M • Dk Blue Line
 *       2 Mary M & Garnet Fuller
 *             Leron       87
 *             Elaine      84
 *         Leron
 *             No children
 *       2 Elaine & Bill Wilson
 *             Wesley      57
 *             ...
 *
 * A couple is the descendant's first name and their own suffix, then the
 * spouse's first and last name with the spouse's own suffix — each person's
 * angel and twin tag right after their own name; a parent with no spouse
 * recorded is their first name and suffix alone. The spouse married in, so
 * their name carries the in-law asterisk the legend keys, before their
 * angel — "Roger, Jr. & Christi Witherspoon*". A
 * parent with children carries their count in the gutter to the left of
 * their name. The Start/Top of Line couple's own parents, as their record
 * names them, print above them. On a desktop the list runs in two columns
 * the way Family Members does: the first fills the visible height and the
 * rest carries on in a second column that scrolls, a line's heading
 * repeated where the split falls inside it. Within a line the groups run generation by generation:
 * the head with their children, then each of those children with theirs,
 * then the grandchildren, and so on down — siblings in birth order, cousins
 * in their parents' order. A child with children of their own always gets a group; an adult
 * without gets one that says "No children"; a minor without appears only as
 * a child. The count beside the title is the dashboard statistic the card
 * prints — descendant parents over parents and children together, "72/184".
 *
 * The shape of the page is the family tree's own, read off
 * /family-tree-hierarchy-v2 (oldest first within each generation), with the
 * report's ages laid over the children — see dashboardDescParentChildren.js.
 * /dashboard-statistics-with-details is read for the photos and
 * multiple-birth tags, which come off each member's roster record.
 *
 * The filter bar carries the sorts alone — A–Z, Z–A, Line Color and the two
 * age sorts — and no member tabs, since every row is a descendant. The order
 * the page opens in is the family's, so nothing is lit for it. Line Color
 * reads the lines by colour name instead of by the head's age; the other
 * four keep the lines and groups where they are and re-order the children
 * inside each group. The search box reads every name, line colour and age
 * on the page: a match on a line's heading keeps the whole line, on a
 * parent's heading the whole group, and otherwise only the children that
 * match. See docs/dashboard-filter-rules.md.
 */

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import useDashboardPhoneLayout from "./useDashboardPhoneLayout";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  DashboardDeceasedAngel,
  DashboardInLawAsterisk,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  DashboardPhotoModal,
  DashboardPhotoName,
  getDashboardMemberNamePhotoUrls,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import {
  EMPTY_REPORT,
  buildDescParentChildrenPage,
  formatDescParentChildrenCount,
  fetchDashboardStatistics,
  fetchDescParentChildren,
  fetchDescParentFamilyTree,
  splitDescParentChildrenBlocks,
  toDescParentChildrenBlocks,
} from "../../../Utils/dashboardDescParentChildren";
import {
  dashboardSearchMatches,
  getDashboardMemberSearchText,
} from "../../../Utils/dashboardDetailSearch";
import { getMemberDobTimestamp } from "../../../Utils/family-tree/dateAndTime";
import {
  buildMultipleBirthSetSizes,
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
  getMultipleBirthTagForSize,
  getSpouseMultipleBirthSlice,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import { isLightLineColor } from "../../../Utils/colors";

/** The order the page opens in is the family's own, so no sort is lit for it. */
const DEFAULT_SORT = "none";

/** Printed where no age can be read. */
const MISSING_VALUE = "—";

/** Unknown numbers sort last whichever way the column is read. */
const compareNumbers = (a, b, { descending = false } = {}) => {
  if (a === b) return 0;
  if (a === null || a === undefined) return 1;
  if (b === null || b === undefined) return -1;
  return descending ? b - a : a - b;
};

const lineLabel = (colorName) => (colorName ? `${colorName} Line` : "");

/**
 * Everything a child row can be found by: the name as printed and the
 * record's own name parts, the age, and the line colour.
 */
const rowSearchText = (row) =>
  [
    getDashboardMemberSearchText(row.member, row.sortName),
    row.ageLabel ?? "",
    lineLabel(row.colorName),
  ].join(" ");

function DescParentChildren() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } =
    useDashboardPhotoModal();
  const isPhone = useDashboardPhoneLayout();
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [searchQuery, setSearchQuery] = useState("");
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isPhone && rosterLoading);
  const [report, setReport] = useState(EMPTY_REPORT);
  const [treeRoots, setTreeRoots] = useState([]);
  // The roster by member id: neither the tree nor the report sends the
  // multiple-birth fields, and the roster's photo is the one every other
  // list opens, so both are read off the member's roster record.
  const [rosterById, setRosterById] = useState(() => new Map());
  // The phone's footer key, as on Family Members: every family line in the
  // tree with its head, built from the roster rather than this page's rows.
  const [legendLines, setLegendLines] = useState([]);
  // The dashboard statistics block, which the heading count is read from so
  // the page prints the figure its card did; it settles on its own.
  const [statistics, setStatistics] = useState(null);

  // The report and the tree are the two halves of the page, so it waits for
  // both; the roster settles on its own and only adds the markers.
  useEffect(() => {
    let cancelled = false;

    const fetchPage = async () => {
      const family_tree_id = getFamilyTreeIdFromStorage();
      try {
        setLoading(true);
        const [nextReport, roots] = await Promise.all([
          fetchDescParentChildren(family_tree_id).catch((error) => {
            console.error("Error fetching descendant parents and children:", error);
            return EMPTY_REPORT;
          }),
          fetchDescParentFamilyTree(family_tree_id).catch((error) => {
            console.error("Error fetching family tree:", error);
            return [];
          }),
        ]);
        if (cancelled) return;
        setReport(nextReport);
        setTreeRoots(roots);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPage();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setRosterById(
          new Map(
            members
              .map((member) => [String(member?.id ?? member?.member_id ?? ""), member])
              .filter(([id]) => id)
          )
        );
        setLegendLines(
          buildDashboardColorLegendLines(
            members,
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  useEffect(() => {
    let cancelled = false;

    fetchDashboardStatistics(getFamilyTreeIdFromStorage())
      .then((next) => {
        if (!cancelled) setStatistics(next);
      })
      .catch((error) => {
        console.error("Error fetching dashboard statistics:", error);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const page = useMemo(
    () => buildDescParentChildrenPage(treeRoots, report.ageById),
    [treeRoots, report.ageById]
  );

  /**
   * The record a name's markers and photo are read from: the roster record
   * where the roster knows the member, else the tree's own node.
   */
  const getMarkerMember = useCallback(
    (entry) => rosterById.get(entry.id) ?? entry.member,
    [rosterById]
  );

  /**
   * Every member's multiple-birth set size, read across the roster and the
   * tree together: a twin whose own record says nothing is still tagged when
   * the other twin's record links to them, as the family tree draws them.
   */
  const multipleBirthSetSizes = useMemo(() => {
    const treeNodes = [];
    const walk = (nodes) =>
      (Array.isArray(nodes) ? nodes : []).forEach((node) => {
        treeNodes.push(node);
        walk(node?.children);
      });
    walk(treeRoots);
    return buildMultipleBirthSetSizes([...rosterById.values(), ...treeNodes]);
  }, [rosterById, treeRoots]);

  /** A member's twin/trip/… tag, from either of their records or a sibling's link. */
  const getMultipleBirthTag = useCallback(
    (entry) =>
      getMultipleBirthSuperscriptText(rosterById.get(entry.id)) ||
      getMultipleBirthSuperscriptText(entry.member) ||
      getMultipleBirthTagForSize(multipleBirthSetSizes.get(entry.id)),
    [rosterById, multipleBirthSetSizes]
  );

  const isSearching = searchQuery.trim().length > 0;

  /**
   * What the search leaves on screen. A match on a line's heading — the
   * head's name or the line colour — keeps the whole line; on a parent's
   * heading, the whole group; otherwise only the children that match. The
   * Start/Top of Line section keeps the children whose name or line matches,
   * or all of them when the couple's own heading does. Empty groups and
   * lines drop out; a blank search is the whole page.
   */
  const searchedPage = useMemo(() => {
    const query = searchQuery.trim();
    if (!query) return page;

    const topOfLine = page.topOfLine
      .map((section) => ({
        ...section,
        rows: dashboardSearchMatches(
          `${section.headingName} ${lineLabel(section.colorName)}`,
          query
        )
          ? section.rows
          : section.rows.filter((row) => dashboardSearchMatches(rowSearchText(row), query)),
      }))
      .filter((section) => section.rows.length > 0);

    const lines = page.lines
      .map((line) => {
        if (
          dashboardSearchMatches(`${line.headName} ${lineLabel(line.colorName)}`, query)
        ) {
          return line;
        }
        return {
          ...line,
          groups: line.groups
            .map((group) =>
              dashboardSearchMatches(group.headingName, query)
                ? group
                : {
                    ...group,
                    rows: group.rows.filter((row) =>
                      dashboardSearchMatches(rowSearchText(row), query)
                    ),
                  }
            )
            .filter(
              (group) =>
                group.rows.length > 0 ||
                dashboardSearchMatches(group.headingName, query)
            ),
        };
      })
      .filter((line) => line.groups.length > 0);

    return { topOfLine, lines };
  }, [page, searchQuery]);

  /**
   * The page in the order the sort reads. Line Color re-orders the lines by
   * colour name, and the Start/Top of Line children with them, since each
   * child is a line; every other sort leaves the lines and groups where the
   * family puts them and re-orders the children inside each group. With
   * nothing lit the page stands as the tree sends it: oldest first
   * throughout. Sorting is stable, so ties keep the tree's order.
   */
  const sortedPage = useMemo(() => {
    // Children of the same age are read by date of birth. Unknown sorts last.
    const compareDob = (a, b, { oldestFirst }) => {
      const left = getMemberDobTimestamp(a.member);
      const right = getMemberDobTimestamp(b.member);
      if (left === right) return 0;
      if (left === -1) return 1;
      if (right === -1) return -1;
      return oldestFirst ? left - right : right - left;
    };
    const byAge = (descending) => (a, b) =>
      compareNumbers(a.age, b.age, { descending }) ||
      compareDob(a, b, { oldestFirst: descending }) ||
      a.sortName.localeCompare(b.sortName);
    const byColor = (a, b) => a.colorName.localeCompare(b.colorName);
    // Rows under Line Color: grouped by colour, A–Z by name within each.
    const byColorThenName = (a, b) => byColor(a, b) || a.sortName.localeCompare(b.sortName);

    const sortRows = (rows) => {
      const sorted = [...rows];
      switch (sortBy) {
        case "oldest":
          return sorted.sort(byAge(true));
        case "youngest":
          return sorted.sort(byAge(false));
        case "alpha-asc":
          return sorted.sort((a, b) => a.sortName.localeCompare(b.sortName));
        case "alpha-desc":
          return sorted.sort((a, b) => b.sortName.localeCompare(a.sortName));
        case "color":
          return sorted.sort(byColorThenName);
        default:
          return sorted;
      }
    };

    const topOfLine = searchedPage.topOfLine.map((section) => ({
      ...section,
      rows: sortRows(section.rows),
    }));

    if (sortBy === "color") {
      return {
        topOfLine,
        lines: [...searchedPage.lines].sort(byColor).map((line) => ({
          ...line,
          groups: line.groups.map((group) => ({ ...group, rows: sortRows(group.rows) })),
        })),
      };
    }

    return {
      topOfLine,
      lines: searchedPage.lines.map((line) => ({
        ...line,
        groups: line.groups.map((group) => ({ ...group, rows: sortRows(group.rows) })),
      })),
    };
  }, [searchedPage, sortBy]);

  const visibleGroups = useMemo(
    () => sortedPage.lines.flatMap((line) => line.groups),
    [sortedPage]
  );

  /** Every member named on the page, for the footer's multiple-birth key. */
  const visibleMembers = useMemo(
    () => [
      ...sortedPage.topOfLine.flatMap((section) => [section, ...section.rows]),
      ...visibleGroups.flatMap((group) => [group, ...group.rows]),
    ],
    [sortedPage, visibleGroups]
  );

  const isEmpty =
    sortedPage.topOfLine.length === 0 && sortedPage.lines.length === 0;

  const blocks = useMemo(() => toDescParentChildrenBlocks(sortedPage), [sortedPage]);

  // The two-column split, as Family Members does it: the blocks are laid out
  // once more in a hidden measuring list, and the first column takes as many
  // whole blocks as fit the columns' visible height; the rest scroll in the
  // second column. A phone never splits — one scrolling column instead.
  const columnsRef = useRef(null);
  const measureListRef = useRef(null);
  const firstColumnRef = useRef(null);
  const [splitAt, setSplitAt] = useState(0);
  // Headings are zero-wide in the grid (see App.css), so the first column
  // cannot size itself to them: its width is measured off the same list.
  const [firstColumnWidth, setFirstColumnWidth] = useState(null);
  // The split the measurement last asked for. The trim effect below only
  // acts once the rendered split has caught up with it — the commit that
  // swaps the blocks still shows the old split, and trimming that stale
  // column would eat into the fresh measurement one row per render.
  const measuredSplitRef = useRef(0);

  useLayoutEffect(() => {
    if (loading || blocks.length === 0 || isPhone) {
      measuredSplitRef.current = blocks.length;
      setSplitAt(blocks.length);
      setFirstColumnWidth(null);
      return undefined;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return undefined;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      const wrappers = Array.from(measureEl.children);
      if (availableHeight <= 0 || wrappers.length === 0) {
        measuredSplitRef.current = blocks.length;
        setSplitAt(blocks.length);
        setFirstColumnWidth(null);
        return;
      }

      let cumulative = 0;
      let nextSplit = wrappers.length;
      for (let index = 0; index < wrappers.length; index += 1) {
        cumulative += wrappers[index].offsetHeight;
        if (cumulative > availableHeight) {
          nextSplit = Math.max(1, index);
          break;
        }
      }

      // scrollWidth reads the whole block, the overflowing headings included.
      const width = wrappers
        .slice(0, nextSplit)
        .reduce((widest, wrapper) => Math.max(widest, wrapper.scrollWidth), 0);
      // The column is border-box, so its air before the rule goes on top of
      // the measure — else it would come out of the widest row instead.
      const firstColEl = firstColumnRef.current;
      const paddingRight = firstColEl
        ? parseFloat(getComputedStyle(firstColEl).paddingRight) || 0
        : 0;

      measuredSplitRef.current = nextSplit;
      setSplitAt(nextSplit);
      setFirstColumnWidth(
        nextSplit < wrappers.length ? Math.ceil(width + paddingRight) : null
      );
    };

    calculateSplit();

    // Measured again whenever the visible height changes or the blocks
    // themselves do — a web font landing, or the roster's markers arriving
    // after the tree, both re-flow the rows.
    const observer = new ResizeObserver(calculateSplit);
    observer.observe(columnsEl);
    observer.observe(measureEl);
    window.addEventListener("resize", calculateSplit);
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) calculateSplit();
    });

    return () => {
      cancelled = true;
      observer.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [blocks, loading, isPhone]);

  // A phone is always one column, whatever split a wider window left behind.
  const columns = useMemo(
    () => splitDescParentChildrenBlocks(blocks, isPhone ? blocks.length : splitAt),
    [blocks, splitAt, isPhone]
  );
  const isSingleColumn = columns.length === 1;

  // After render, trim the first column if its last row still runs past the
  // visible area — the shared grid can sit a row taller than the measure.
  useLayoutEffect(() => {
    if (loading || isPhone || isSingleColumn) return;
    if (splitAt !== measuredSplitRef.current) return;

    const columnsEl = columnsRef.current;
    const firstColEl = firstColumnRef.current;
    if (!columnsEl || !firstColEl) return;

    const cells = firstColEl.querySelectorAll(
      ".desc-parent-children-cell, .desc-parent-children-heading"
    );
    const lastCell = cells[cells.length - 1];
    if (!lastCell) return;

    const columnsBottom = columnsEl.getBoundingClientRect().bottom;
    if (lastCell.getBoundingClientRect().bottom > columnsBottom + 0.5) {
      const trimmed = Math.max(1, splitAt - 1);
      measuredSplitRef.current = trimmed;
      setSplitAt(trimmed);
    }
  }, [columns, loading, isPhone, isSingleColumn, splitAt]);

  // The second column is only as wide as its longest row plus its four-space
  // padding and the scrollbar, so the scrollbar sits by the list and the room
  // to its right stays free. Measured off the rendered rows, since the
  // headings are zero-wide in the grid and overflow it.
  const secondColumnRef = useRef(null);
  const [secondColumnWidth, setSecondColumnWidth] = useState(null);

  useLayoutEffect(() => {
    if (loading || isPhone || isSingleColumn) {
      setSecondColumnWidth(null);
      return undefined;
    }

    const measure = () => {
      const columnEl = secondColumnRef.current;
      if (!columnEl) return;
      const columnLeft = columnEl.getBoundingClientRect().left;
      const contentRight = Array.from(columnEl.children).reduce((widest, child) => {
        const right = child.getBoundingClientRect().left + child.scrollWidth - columnLeft;
        return Math.max(widest, right);
      }, 0);
      const style = getComputedStyle(columnEl);
      const borders =
        (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
      const scrollbar = columnEl.offsetWidth - columnEl.clientWidth - borders;
      const paddingRight = parseFloat(style.paddingRight) || 0;
      setSecondColumnWidth(Math.ceil(contentRight + paddingRight + scrollbar));
    };

    measure();
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [columns, loading, isPhone, isSingleColumn]);

  // With two columns the search + sort block is centred over the pair of
  // them — from the first column's left edge to the second's right edge —
  // so it sits evenly between the two. With no column rule (one column, or
  // a phone, where the row is display: contents) the block keeps its place
  // at the left. The shift is clamped so the block never runs past either
  // edge of its line.
  const filterRowRef = useRef(null);
  const columnDividerRef = useRef(null);

  useLayoutEffect(() => {
    const filterRowEl = filterRowRef.current;
    if (!filterRowEl) return undefined;

    const centreFilterRow = () => {
      filterRowEl.style.paddingLeft = "";
      const dividerEl = columnDividerRef.current;
      const firstColumnEl = firstColumnRef.current;
      const secondColumnEl = secondColumnRef.current;
      if (
        !dividerEl ||
        !firstColumnEl ||
        !secondColumnEl ||
        !window.matchMedia("(min-width: 769px)").matches
      )
        return;

      const rowRect = filterRowEl.getBoundingClientRect();
      const blockWidth = Math.max(
        ...Array.from(filterRowEl.children).map((child) => child.getBoundingClientRect().width)
      );
      const columnsLeft = firstColumnEl.getBoundingClientRect().left;
      const columnsRight = secondColumnEl.getBoundingClientRect().right;
      const columnsCentre = (columnsLeft + columnsRight) / 2 - rowRect.left;
      const maxOffset = Math.max(0, rowRect.width - blockWidth);
      // The block sits a little left of the exact centre, per the client.
      const leftNudge = 40;
      const offset = Math.min(
        maxOffset,
        Math.max(0, columnsCentre - blockWidth / 2 - leftNudge)
      );

      filterRowEl.style.paddingLeft = `${offset}px`;
    };

    centreFilterRow();

    const observer = new ResizeObserver(centreFilterRow);
    if (columnsRef.current) observer.observe(columnsRef.current);
    window.addEventListener("resize", centreFilterRow);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", centreFilterRow);
      filterRowEl.style.paddingLeft = "";
    };
  }, [columns, loading, isSingleColumn, firstColumnWidth, secondColumnWidth, searchQuery]);

  /** A name with its markers: photo, angel, multiple-birth tag. */
  const renderName = (entry) => {
    const markerMember = getMarkerMember(entry);
    const multipleBirthSup = getMultipleBirthTag(entry);

    return (
      <DashboardNameMarkerGroup>
        <DashboardPhotoName
          {...getDashboardMemberNamePhotoUrls(markerMember)}
          onViewPhoto={openPhoto}
        >
          <DashboardMemberNameLabel
            baseName={entry.baseName}
            descendantSpouseName={entry.descendantSpouseName}
          />
        </DashboardPhotoName>
        {entry.isDeceased && <DashboardDeceasedAngel />}
        {multipleBirthSup && (
          <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
        )}
      </DashboardNameMarkerGroup>
    );
  };

  /**
   * A couple heading: the descendant's name with their own suffix, angel and
   * twin tag, then "& " and the spouse's name with the in-law asterisk and
   * their own angel and twin tag — or the
   * descendant alone. A parent with children carries the family's count in
   * the gutter to the left of the name — the parent or parents named plus
   * their children, and their own name — the descendant's,
   * never the spouse's — is set heavier than the rest of the heading. The
   * generation 1 couple founds the tree, so neither of them is an in-law and
   * the spouse there goes without the asterisk.
   */
  const renderCoupleHeading = (entry, { showInLawAsterisk = true } = {}) => {
    const { primaryName, spouseName } = entry.headingParts;
    const hasChildren = entry.childCount > 0;
    const markerMember = getMarkerMember(entry);
    const multipleBirthSup = getMultipleBirthTag(entry);
    const spouseMultipleBirthSup = spouseName
      ? getMultipleBirthSuperscriptText(getSpouseMultipleBirthSlice(markerMember))
      : "";

    return (
      <>
        {hasChildren && (
          <span
            className="desc-parent-children-child-count"
            title={`${entry.familyCount} in this family: the parent${
              entry.headingParts.spouseName ? "s" : ""
            } and ${entry.childCount} ${entry.childCount === 1 ? "child" : "children"}`}
          >
            {entry.familyCount}
          </span>
        )}
        <DashboardNameMarkerGroup>
          <DashboardPhotoName
            {...getDashboardMemberNamePhotoUrls(markerMember)}
            onViewPhoto={openPhoto}
          >
            <span
              className={`desc-parent-children-heading-name desc-parent-children-descendant-name${
                hasChildren ? " desc-parent-children-descendant-name-with-children" : ""
              }`}
            >
              {primaryName}
            </span>
          </DashboardPhotoName>
          {entry.isDeceased && <DashboardDeceasedAngel />}
          {multipleBirthSup && (
            <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
          )}
          {spouseName && (
            <>
              <span className="desc-parent-children-heading-name dashboard-detail-couple-joiner">
                &amp;
              </span>
              <span className="desc-parent-children-heading-name">{spouseName}</span>
              {showInLawAsterisk && <DashboardInLawAsterisk />}
              {entry.spouseDeceased && <DashboardDeceasedAngel />}
              {spouseMultipleBirthSup && (
                <sup className="dashboard-multiple-birth-sup">{spouseMultipleBirthSup}</sup>
              )}
            </>
          )}
        </DashboardNameMarkerGroup>
      </>
    );
  };

  /**
   * A line as the report prints it: a bullet and the colour's name, in that
   * colour — "• Dk Blue Line" — after the head's name where one is given,
   * which takes the same colour.
   */
  const renderLine = (entry, headName = "") => (
    <>
      {headName ? (
        <span className="desc-parent-children-line-head" style={{ color: entry.hexCode }}>
          {headName}
        </span>
      ) : null}
      {entry.colorName ? (
        <span
          className="desc-parent-children-line-name"
          style={{ color: entry.hexCode }}
        >
          {`• ${entry.colorName} Line`}
        </span>
      ) : null}
    </>
  );

  /** A child under a parent: name, then age. */
  const renderChildRow = (row) => (
    <React.Fragment key={row.id}>
      <div className="desc-parent-children-cell desc-parent-children-name desc-parent-children-child-name">
        {renderName(row)}
      </div>
      <div className="desc-parent-children-cell desc-parent-children-age">
        {row.ageLabel ?? MISSING_VALUE}
      </div>
    </React.Fragment>
  );

  /**
   * A Start/Top of Line child: their name, the age they would be today in
   * the same column the children's ages sit in below, then the line they
   * head.
   */
  const renderHeadRow = (row) => (
    <React.Fragment key={row.id}>
      <div className="desc-parent-children-cell desc-parent-children-name desc-parent-children-head-name">
        {renderName(row)}
      </div>
      <div className="desc-parent-children-cell desc-parent-children-age">
        {row.ageLabel ?? MISSING_VALUE}
      </div>
      <div className="desc-parent-children-cell desc-parent-children-head-line">
        {renderLine(row)}
      </div>
    </React.Fragment>
  );

  /**
   * The Start/Top of Line couple: the parents their record names over them,
   * then the couple in their own line colour, and the heads of line under.
   */
  const renderTopSection = (section) => (
    <>
      {section.parentsName && (
        <div className="desc-parent-children-heading desc-parent-children-top-parents">
          <span className="desc-parent-children-top-parents-label">
            {`Parents of ${section.headingParts.primaryName}:`}
          </span>
          <span className="desc-parent-children-top-parents-name">{section.parentsName}</span>
        </div>
      )}
      <div
        className={`desc-parent-children-heading desc-parent-children-top-heading${
          isLightLineColor(section.hexCode) ? " desc-parent-children-heading--light" : ""
        }`}
      >
        {renderCoupleHeading(section, { showInLawAsterisk: false })}
        <span className="desc-parent-children-heading-line">{renderLine(section)}</span>
      </div>
      {section.rows.map(renderHeadRow)}
    </>
  );

  /**
   * A line's heading: the line's total members in the gutter, then the
   * head's name and colour, over its groups. The total is the whole line's,
   * whatever a search leaves on screen.
   */
  const renderLineHeading = (line, continued) => (
    <div
      className={`desc-parent-children-heading desc-parent-children-line-heading${
        isLightLineColor(line.hexCode) ? " desc-parent-children-heading--light" : ""
      }`}
    >
      <span
        className="desc-parent-children-child-count desc-parent-children-line-count"
        title={`${line.memberCount} in this line`}
      >
        {line.memberCount}
      </span>
      {renderLine(line, line.headName)}
      {continued && <span className="desc-parent-children-line-continued">(cont.)</span>}
    </div>
  );

  /** A parent with their children under them, or "No children". */
  const renderGroup = (group) => (
    <>
      <div className="desc-parent-children-heading desc-parent-children-parent-row">
        {renderCoupleHeading(group)}
      </div>
      {group.rows.length > 0 ? (
        group.rows.map(renderChildRow)
      ) : (
        <div className="desc-parent-children-cell desc-parent-children-name desc-parent-children-child-name desc-parent-children-no-children">
          No children
        </div>
      )}
    </>
  );

  const renderBlock = (block) => (
    <React.Fragment key={block.key}>
      {block.kind === "top" && renderTopSection(block.section)}
      {block.kind === "line" && renderLineHeading(block.line, block.continued)}
      {block.kind === "group" && renderGroup(block.group)}
    </React.Fragment>
  );

  return (
    <div className="dashboard-container desc-parent-children-page dashboard-detail-page">
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">Parent(s) and Child(ren)</h3>
            {/* The figure the dashboard card prints — parents over parents
                and children together, "72/184" — or how many groups a
                search leaves. */}
            <div className="dashboard-detail-count">
              {isSearching
                ? visibleGroups.length
                : formatDescParentChildrenCount(statistics)}
            </div>
            {/* The search box takes its own line, centred over the sort bar,
                and the pair is centred on the column rule below. */}
            {!loading && (
              <div ref={filterRowRef} className="desc-parent-children-filter-row">
                <DashboardDetailSearchBox
                  centreOverDivider={false}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search name, line color, age"
                  ariaLabel="Search by name, line color or age"
                />
                <DashboardDetailSortFilterBar
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  showMemberFilters={false}
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading parents and children...</p>
          </div>
        ) : isEmpty ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="desc-parent-children-page dashboard-detail-content-wrapper">
            {/* Hidden copy of every block, laid out once each so the split
                can be measured against the visible height. */}
            {!isPhone && (
              <div
                ref={measureListRef}
                className="desc-parent-children-list-measure"
                aria-hidden="true"
              >
                {blocks.map((block) => (
                  <div
                    key={block.key}
                    className="desc-parent-children-list dashboard-detail-list"
                  >
                    {renderBlock(block)}
                  </div>
                ))}
              </div>
            )}

            <div ref={columnsRef} className="desc-parent-children-columns">
              <div
                ref={firstColumnRef}
                className={`desc-parent-children-list dashboard-detail-list desc-parent-children-list-first${
                  isSingleColumn ? " desc-parent-children-list--scrollable" : ""
                }`}
                style={
                  isSingleColumn || firstColumnWidth === null
                    ? undefined
                    : { width: `${firstColumnWidth}px`, minWidth: `${firstColumnWidth}px` }
                }
              >
                {columns[0].map(renderBlock)}
              </div>
              {!isSingleColumn && (
                <>
                  <div ref={columnDividerRef} className="desc-parent-children-column-divider" />
                  <div
                    ref={secondColumnRef}
                    className="desc-parent-children-list dashboard-detail-list desc-parent-children-list-scroll"
                    style={
                      secondColumnWidth === null
                        ? undefined
                        : { flex: "0 0 auto", width: `${secondColumnWidth}px` }
                    }
                  >
                    {columns[1].map(renderBlock)}
                  </div>
                </>
              )}
            </div>

            <div className="desc-parent-children-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(
                  visibleMembers,
                  getMultipleBirthTag
                )}
              />
              {isPhone && legendLines.length > 0 && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>

      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default DescParentChildren;
